import { useEffect, useState } from "react";

import { Encounter } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { ActionIcon, Group, Stack } from "@mantine/core";

import { IconPencil } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { resolveEncounterProviderDisplay } from "@/encounters/services/encounter-provider-display";
import { formatEncounterServiceDate } from "@/encounters/utils/encounter-timezone";
import { DevelopmentFeatureFlags, FeatureGate, PracticeFeatureFlags } from "@/feature-flags";

interface EncounterBillingHeaderProps {
  encounter: Encounter;
  paymentMethodDisplay: string;
  insuranceDisplay: string | undefined;
  /** The Location to display, resolved by the caller from wherever it is sourced. */
  locationDisplay: string | undefined;
  /**
   * When true, shows the Location pencil. Only the BillerSection passes this — other billing
   * header usages stay read-only.
   */
  canEditLocation?: boolean;
  /** Opens the Update Location drawer. Ignored unless `canEditLocation` is true. */
  onEditLocation?: () => void;
}

function InsurancePlaceholder() {
  return (
    <Stack gap="xs">
      <TextWrapper fw={600} c="gray.7">
        Insurance
      </TextWrapper>
      <TextWrapper c="dark.9" fw="normal">
        --
      </TextWrapper>
    </Stack>
  );
}

const EncounterBillingHeader = ({
  encounter,
  paymentMethodDisplay,
  insuranceDisplay,
  locationDisplay,
  canEditLocation = false,
  onEditLocation,
}: EncounterBillingHeaderProps) => {
  const medplum = useMedplum();
  const [providerDisplay, setProviderDisplay] = useState("");
  const serviceDate = formatEncounterServiceDate(encounter);
  const encounterType = encounter?.serviceType?.coding?.[0]?.display;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const display = await resolveEncounterProviderDisplay(medplum, encounter);
      if (!cancelled) setProviderDisplay(display);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [medplum, encounter]);

  return (
    <Stack>
      <TextWrapper fw="bold" c="dark.9">
        Encounter Details
      </TextWrapper>
      <Group w="100%" wrap="nowrap" justify="space-between">
        <Stack gap="xs">
          <TextWrapper fw={600} c="gray.7">
            Service Date
          </TextWrapper>
          <TextWrapper c="dark.9" fw="normal">
            {serviceDate}
          </TextWrapper>
        </Stack>
        <Stack gap="xs">
          <TextWrapper fw={600} c="gray.7">
            Type
          </TextWrapper>
          <TextWrapper c="dark.9" fw="normal">
            {encounterType}
          </TextWrapper>
        </Stack>
        <Stack gap="xs">
          <TextWrapper fw={600} c="gray.7">
            Provider
          </TextWrapper>
          <TextWrapper c="dark.9" fw="normal">
            {providerDisplay}
          </TextWrapper>
        </Stack>
        <Stack gap="xs">
          <Group gap="xs" wrap="nowrap" align="center">
            <TextWrapper fw={600} c="gray.7">
              Location
            </TextWrapper>
            {canEditLocation && onEditLocation && (
              <ActionIcon
                size="sm"
                variant="transparent"
                onClick={onEditLocation}
                aria-label="Edit location"
                data-testid="encounter-billing-edit-location"
              >
                <IconPencil size={16} color="var(--mantine-color-gray-6)" />
              </ActionIcon>
            )}
          </Group>
          <TextWrapper c="dark.9" fw="normal" data-testid="encounter-billing-location">
            {locationDisplay}
          </TextWrapper>
        </Stack>
        <FeatureGate fallback={<InsurancePlaceholder />} name={PracticeFeatureFlags.Billing}>
          <FeatureGate
            fallback={<InsurancePlaceholder />}
            name={DevelopmentFeatureFlags.PracticeBilling}
          >
            <Stack gap="xs">
              <TextWrapper fw={600} c="gray.7">
                Payment Method
              </TextWrapper>
              <TextWrapper c="dark.9" fw="normal" data-testid="encounter-billing-payment-method">
                {paymentMethodDisplay}
              </TextWrapper>
            </Stack>
            <Stack gap="xs">
              <TextWrapper fw={600} c="gray.7">
                Insurance
              </TextWrapper>
              <TextWrapper c="dark.9" fw="normal" data-testid="encounter-billing-insurance">
                {insuranceDisplay?.trim() || "--"}
              </TextWrapper>
            </Stack>
          </FeatureGate>
        </FeatureGate>
      </Group>
    </Stack>
  );
};

export default EncounterBillingHeader;
